
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Phone, FileText } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AppointmentActions } from "./AppointmentActions";

interface AppointmentCardProps {
  appointment: any;
  onStatusUpdate: (appointmentId: string, status: string) => void;
  onViewReceipt: (appointmentId: string) => void;
  onViewDetails: (appointment: any) => void;
  onDelete: (appointmentId: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
}

export const AppointmentCard = ({
  appointment,
  onStatusUpdate,
  onViewReceipt,
  onViewDetails,
  onDelete,
  isUpdating,
  isDeleting,
  getStatusColor,
  getStatusText,
}: AppointmentCardProps) => {
  console.log('AppointmentCard - rendering appointment:', appointment?.id);
  console.log('AppointmentCard - appointment status:', appointment?.status);

  if (!appointment) {
    console.log('AppointmentCard - No appointment data provided');
    return null;
  }

  return (
    <Card className="border-l-4 border-l-primary shadow-md hover:shadow-lg transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div className="flex-1 space-y-4">
            {/* En-tête avec patient et statut */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {appointment.patient?.full_name || "Patient inconnu"}
                  </h3>
                  <p className="text-sm text-gray-500">ID: {appointment.id.slice(0, 8)}</p>
                </div>
              </div>
              <Badge className={`${getStatusColor(appointment.status)} px-3 py-1 text-sm font-medium`}>
                {getStatusText(appointment.status)}
              </Badge>
            </div>
            
            {/* Informations du rendez-vous */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Date demandée</p>
                    <p className="font-medium text-gray-800">
                      {format(new Date(appointment.requested_date), "PPPp", { locale: fr })}
                    </p>
                  </div>
                </div>
                
                {appointment.service && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Service</p>
                      <p className="font-medium text-gray-800">{appointment.service.service_name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Informations patient */}
            {appointment.patient && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-600" />
                  Informations patient
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {appointment.patient.phone && (
                    <div>
                      <span className="text-gray-600">Téléphone:</span>
                      <span className="ml-2 font-medium">{appointment.patient.phone}</span>
                    </div>
                  )}
                  {appointment.patient.email && (
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{appointment.patient.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Notes si présentes */}
            {appointment.notes && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  Notes de la demande
                </h4>
                <p className="text-sm text-gray-700 bg-white p-3 rounded border">
                  {appointment.notes}
                </p>
              </div>
            )}

            {/* Date de création */}
            <div className="text-xs text-gray-500">
              Demande créée le {format(new Date(appointment.created_at), "PPPp", { locale: fr })}
            </div>
          </div>
          
          {/* Boutons d'action */}
          <div className="flex-shrink-0 lg:min-w-[300px]">
            <AppointmentActions
              appointment={appointment}
              onStatusUpdate={onStatusUpdate}
              onViewReceipt={onViewReceipt}
              onViewDetails={onViewDetails}
              onDelete={onDelete}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
